/**
 * SubmissionQueue — Coach-facing queue of pending submissions.
 *
 * Shows:
 *   - List of submissions with learner name, scene title, submitted time
 *   - Click to open grading view
 *   - AI pre-grade scores shown (coach can adjust)
 *   - Override controls per criterion
 *   - Overall feedback text area
 *   - Submit grade button
 */
import React, { useEffect, useState, useCallback } from 'react';
import { curriculumAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import CoachLayout from '../../pages/coach/CoachLayout';
import useOrgScope, { filterDomainsByScope } from '../../hooks/useOrgScope';

export default function SubmissionQueue({ domain: propDomain, sceneId }) {
  const { authoringDomains, isPlatformAdmin } = useAuth();
  const { unrestricted, domains: scopeDomains } = useOrgScope();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [typeFilter, setTypeFilter] = useState('');
  const [domainFilter, setDomainFilter] = useState(propDomain || '');
  const [domainCatalog, setDomainCatalog] = useState([]);
  const [capstoneOnly, setCapstoneOnly] = useState([]);

  useEffect(() => {
    curriculumAPI.listDomains()
      .then(({ data }) => setDomainCatalog(Array.isArray(data?.domains) ? data.domains : []))
      .catch(() => setDomainCatalog([]));
  }, []);

  const domains = React.useMemo(() => {
    const catalog = filterDomainsByScope(domainCatalog, {
      unrestricted,
      isPlatformAdmin,
      scopeDomains,
      authoringDomains,
    });
    if (catalog.length) {
      return catalog.map((d) => ({ id: d.domain_id, label: d.display_name || d.domain_id }));
    }
    return (authoringDomains || []).map((id) => ({ id, label: id }));
  }, [domainCatalog, authoringDomains, isPlatformAdmin, unrestricted, scopeDomains]);

  const domain = domainFilter || propDomain;

  const fetchSubmissions = useCallback(() => {
    setLoading(true);
    setError('');
    const token = localStorage.getItem('scenario_os_token');
    const headers = { ...(token ? { Authorization: `Bearer ${token}` } : {}) };
    const params = new URLSearchParams();
    if (filter) params.set('status', filter);
    if (domain) params.set('domain', domain);
    if (sceneId) params.set('scene_id', sceneId);

    const rubricsP = fetch(`/scenario-os/coach/rubrics/submissions?${params}`, { headers })
      .then((r) => (r.ok ? r.json() : r.json().then((d) => Promise.reject(d))))
      .then((data) => data.submissions || [])
      .catch(() => []);

    // Merge capstone queue (approve / request-resubmit) into the same list
    const capParams = new URLSearchParams();
    if (domain) capParams.set('domain', domain);
    if (filter === 'pending' || filter === 'coach_review_pending' || !filter) {
      capParams.set('status', filter === 'ai_graded' ? 'ai_graded' : 'coach_review_pending');
    } else if (filter) {
      capParams.set('status', filter);
    }
    const capstoneP = fetch(`/scenario-os/curriculum/capstone/submissions?${capParams}`, { headers })
      .then((r) => (r.ok ? r.json() : { submissions: [] }))
      .then((data) => (data.submissions || []).map((s) => ({
        ...s,
        submission_kind: s.submission_kind || 'capstone',
        status: s.status || 'coach_review_pending',
      })))
      .catch(() => []);

    Promise.all([rubricsP, capstoneP])
      .then(([rubrics, caps]) => {
        setCapstoneOnly(caps);
        const byId = new Map();
        [...rubrics, ...caps].forEach((s) => {
          const id = s.submission_id;
          if (!id) return;
          const prev = byId.get(id) || {};
          byId.set(id, {
            ...prev,
            ...s,
            submission_kind: s.submission_kind
              || (String(s.scene_id || '').includes('capstone') ? 'capstone' : (s.submission_kind || 'project')),
          });
        });
        let merged = Array.from(byId.values());
        if (typeFilter) {
          merged = merged.filter((s) => {
            const kind = (s.submission_kind || s.scene_type || '').toLowerCase();
            const sid = String(s.scene_id || '').toLowerCase();
            if (typeFilter === 'capstone') return kind === 'capstone' || sid.includes('capstone');
            if (typeFilter === 'project') return kind === 'project' || sid.includes('project');
            if (typeFilter === 'code_lab') return kind === 'code_lab' || sid.includes('code');
            return true;
          });
        }
        setSubmissions(merged);
      })
      .catch((e) => setError(e.detail || e.message || 'Failed to load submissions'))
      .finally(() => setLoading(false));
  }, [filter, domain, sceneId, domainFilter, typeFilter]);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  if (selected) {
    return (
      <GradingView
        submission={selected}
        onBack={() => { setSelected(null); fetchSubmissions(); }}
      />
    );
  }

  return (
    <CoachLayout title="Submissions">
    <div style={containerStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ margin: 0, fontSize: 20, color: '#1f2937' }}>Submission Queue</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={domainFilter}
            onChange={(e) => setDomainFilter(e.target.value)}
            style={domainSelectStyle}
          >
            <option value="">All domains</option>
            {domains.map((d) => (
              <option key={d.id} value={d.id}>{d.label}</option>
            ))}
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            style={domainSelectStyle}
            aria-label="Submission type"
          >
            <option value="">All types</option>
            <option value="capstone">Capstone</option>
            <option value="project">Project</option>
            <option value="code_lab">Code lab</option>
          </select>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={domainSelectStyle}
            aria-label="Status"
          >
            <option value="pending">Pending</option>
            <option value="coach_review_pending">Coach review pending</option>
            <option value="ai_graded">AI graded</option>
            <option value="ai_failed">AI failed</option>
            <option value="reviewed">Reviewed</option>
            <option value="">All statuses</option>
          </select>
        </div>
      </div>

      {loading && <div style={mutedStyle}>Loading submissions…</div>}
      {error && <div style={errorStyle}>{error}</div>}

      {!loading && submissions.length === 0 && (
        <div style={emptyStyle}>
          <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.5 }}>📭</div>
          No {filter || ''} submissions found.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {submissions.map((s) => (
          <button
            key={s.submission_id}
            onClick={() => setSelected(s)}
            style={submissionCardStyle}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, color: '#1f2937', marginBottom: 2 }}>
                  {s.display_name || s.learner_name || s.learner_id}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  {(s.submission_kind || 'submission')} · Scene: {s.scene_id} · {s.domain}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={statusBadge(s.status)}>{s.status}</div>
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                  {s.submitted_at ? new Date(s.submitted_at).toLocaleDateString() : ''}
                </div>
              </div>
            </div>
            {s.text && (
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6, lineHeight: 1.4 }}>
                {s.text.slice(0, 120)}{s.text.length > 120 ? '…' : ''}
              </div>
            )}
            {s.ai_overall_pct != null && (
              <div style={{ fontSize: 12, color: '#3b82f6', marginTop: 4, fontWeight: 600 }}>
                AI Score: {s.ai_overall_pct}%
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
    </CoachLayout>
  );
}


function GradingView({ submission, onBack }) {
  const [rubric, setRubric] = useState(null);
  const [scores, setScores] = useState([]);
  const [feedback, setFeedback] = useState('');
  const [saving, setSaving] = useState(false);
  const [aiGrading, setAiGrading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const token = localStorage.getItem('scenario_os_token');
  const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };

  useEffect(() => {
    fetch(`/scenario-os/coach/rubrics/${encodeURIComponent(submission.scene_id)}`, { headers })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data) {
          setRubric(data);
          const existingScores = submission.scores || submission.ai_scores || [];
          const initial = (data.criteria || []).map((c) => {
            const existing = existingScores.find((s) => s.criterion_name === c.name);
            return {
              criterion_name: c.name,
              score: existing?.score ?? 0,
              feedback: existing?.feedback || existing?.reasoning || '',
            };
          });
          setScores(initial);
        }
      })
      .catch(() => {});
    setFeedback(submission.overall_feedback || '');
  }, [submission]);

  const triggerAiGrade = async () => {
    setAiGrading(true);
    setError('');
    try {
      const res = await fetch(
        `/scenario-os/coach/rubrics/submissions/${encodeURIComponent(submission.submission_id)}/ai-grade`,
        { method: 'POST', headers }
      );
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      if (data.ai_scores) {
        setScores((prev) =>
          prev.map((s) => {
            const ai = data.ai_scores.find((a) => a.criterion_name === s.criterion_name);
            if (ai) return { ...s, score: ai.score, feedback: ai.reasoning || ai.feedback || s.feedback };
            return s;
          })
        );
      }
    } catch (e) {
      setError(e.message || 'AI grading failed.');
    } finally {
      setAiGrading(false);
    }
  };

  const updateScore = (idx, field, value) => {
    setScores((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  };

  const isCapstone = (submission.submission_kind || '').toLowerCase() === 'capstone'
    || String(submission.scene_id || '').includes('capstone');
  const reviewLevels = submission.review_levels
    ?? submission.grading_config?.review_levels
    ?? (isCapstone ? 2 : 1);
  // review_levels: 1 = coach grades, 2 = AI then coach approve (no manual criterion grade required)
  const allowManualGrade = !isCapstone && Number(reviewLevels) !== 2;

  const submitGrade = async () => {
    if (!allowManualGrade) {
      setError('This submission is AI-graded; use Approve or Request Resubmission.');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await fetch(
        `/scenario-os/coach/rubrics/submissions/${encodeURIComponent(submission.submission_id)}/grade`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify({ scores, overall_feedback: feedback }),
        }
      );
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${res.status}`);
      }
      setSuccess('Grade submitted successfully.');
      setTimeout(onBack, 1500);
    } catch (e) {
      setError(e.message || 'Failed to submit grade.');
    } finally {
      setSaving(false);
    }
  };

  const approveCapstone = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await fetch(
        `/scenario-os/curriculum/capstone/${encodeURIComponent(submission.submission_id)}/approve`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ coach_feedback: feedback }),
        },
      );
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${res.status}`);
      }
      setSuccess('Approved.');
      setTimeout(onBack, 1200);
    } catch (e) {
      setError(e.message || 'Approve failed');
    } finally {
      setSaving(false);
    }
  };

  const requestResubmit = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await fetch(
        `/scenario-os/curriculum/capstone/${encodeURIComponent(submission.submission_id)}/request-resubmit`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ coach_note: feedback }),
        },
      );
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${res.status}`);
      }
      setSuccess('Resubmission requested.');
      setTimeout(onBack, 1200);
    } catch (e) {
      setError(e.message || 'Request resubmit failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={containerStyle}>
      <button onClick={onBack} style={backBtnStyle}>← Back to Queue</button>

      <div style={{ marginTop: 12, marginBottom: 20 }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 20, color: '#1f2937' }}>
          Grade Submission
        </h2>
        <div style={{ fontSize: 13, color: '#6b7280' }}>
          Learner: <strong>{submission.learner_id}</strong> · Scene: {submission.scene_id}
        </div>
      </div>

      {/* Submission text */}
      <div style={submissionTextStyle}>
        <div style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, color: '#475569', marginBottom: 6 }}>
          Learner Submission
        </div>
        <div style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap', color: '#1f2937' }}>
          {submission.text || '(No text submitted)'}
        </div>
        {submission.file_url && (
          <a href={submission.file_url} target="_blank" rel="noopener noreferrer"
             style={{ display: 'inline-block', marginTop: 8, color: '#3b82f6', fontSize: 13 }}>
            View attached file
          </a>
        )}
      </div>

      {/* AI grade button */}
      <button
        onClick={triggerAiGrade}
        disabled={aiGrading}
        style={aiGradeBtnStyle}
      >
        {aiGrading ? 'AI is grading…' : 'Run AI Pre-Grade'}
      </button>

      {/* Per-criterion grading */}
      {rubric && scores.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <h3 style={sectionTitleStyle}>Criteria Scores</h3>
          {scores.map((s, i) => {
            const criterion = (rubric.criteria || []).find((c) => c.name === s.criterion_name) || {};
            const levels = criterion.levels || [];

            return (
              <div key={i} style={criterionGradeStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <div>
                    <span style={{ fontWeight: 600, fontSize: 14, color: '#1f2937' }}>
                      {s.criterion_name}
                    </span>
                    {criterion.description && (
                      <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 8 }}>
                        {criterion.description}
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: 12, color: '#6b7280' }}>
                    Weight: {criterion.weight || 1}
                  </span>
                </div>

                <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap' }}>
                  {levels.map((l, j) => (
                    <button
                      key={j}
                      onClick={() => updateScore(i, 'score', l.score)}
                      style={{
                        padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                        cursor: 'pointer', transition: 'all 0.1s',
                        border: s.score === l.score ? '2px solid #3b82f6' : '1px solid #d1d5db',
                        background: s.score === l.score ? '#eff6ff' : '#fff',
                        color: s.score === l.score ? '#1d4ed8' : '#374151',
                      }}
                    >
                      {l.label} ({l.score})
                    </button>
                  ))}
                </div>

                <textarea
                  value={s.feedback}
                  onChange={(e) => updateScore(i, 'feedback', e.target.value)}
                  placeholder="Feedback for this criterion…"
                  rows={2}
                  style={feedbackInputStyle}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Overall feedback */}
      <div style={{ marginTop: 16 }}>
        <h3 style={sectionTitleStyle}>Overall Feedback</h3>
        <textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="Write overall feedback for the learner…"
          rows={4}
          style={{ ...feedbackInputStyle, fontSize: 14 }}
        />
      </div>

      {/* Capstone approve / resubmit (merged from Capstone Approvals) */}
      {isCapstone && (
        <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={approveCapstone}
            disabled={saving}
            style={{ ...submitBtnStyle, background: '#16a34a' }}
          >
            {saving ? 'Saving…' : 'Approve'}
          </button>
          <button
            type="button"
            onClick={requestResubmit}
            disabled={saving}
            style={{ ...submitBtnStyle, background: '#dc2626' }}
          >
            Request Resubmission
          </button>
        </div>
      )}

      {/* Submit manual grade only when scene config expects coach scoring */}
      <div style={{ marginTop: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
        {allowManualGrade && (
          <button onClick={submitGrade} disabled={saving} style={submitBtnStyle}>
            {saving ? 'Submitting…' : 'Submit Grade'}
          </button>
        )}
        <button onClick={onBack} style={cancelBtnStyle}>Cancel</button>
      </div>

      {error && <div style={errorBannerStyle}>{error}</div>}
      {success && <div style={successBannerStyle}>{success}</div>}
    </div>
  );
}


// ── Styles ───────────────────────────────────────────────────────────────

const containerStyle = {
  fontFamily: 'system-ui, -apple-system, sans-serif',
  maxWidth: 780,
  margin: '0 auto',
  padding: 20,
};
const mutedStyle = { color: '#6b7280', fontSize: 14, padding: '24px 0' };
const errorStyle = {
  color: '#b3261e', background: 'rgba(239,68,68,0.08)',
  border: '1px solid rgba(239,68,68,0.3)', padding: 12, borderRadius: 6, fontSize: 14,
};
const emptyStyle = {
  textAlign: 'center', color: '#6b7280', fontSize: 14, padding: '32px 0',
};
const domainSelectStyle = {
  padding: '5px 10px', borderRadius: 6, border: '1px solid #d1d5db',
  fontSize: 13, color: '#1e293b', background: '#fff',
};
const filterBtnStyle = {
  border: 'none', borderRadius: 6, padding: '5px 12px',
  fontSize: 12, fontWeight: 600, cursor: 'pointer',
  textTransform: 'capitalize',
};
const submissionCardStyle = {
  width: '100%', textAlign: 'left', background: '#fff',
  border: '1px solid #e5e7eb', borderRadius: 10, padding: 14,
  cursor: 'pointer', transition: 'border-color 0.15s',
  fontFamily: 'inherit', fontSize: 'inherit',
};
const backBtnStyle = {
  background: 'none', border: 'none', color: '#3b82f6',
  cursor: 'pointer', fontSize: 14, padding: 0, fontWeight: 500,
};
const submissionTextStyle = {
  background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10,
  padding: 16, marginBottom: 16,
};
const aiGradeBtnStyle = {
  background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
  color: '#fff', border: 'none', borderRadius: 8,
  padding: '10px 20px', fontSize: 13, fontWeight: 700,
  cursor: 'pointer', width: '100%',
};
const sectionTitleStyle = {
  fontSize: 13, fontWeight: 700, color: '#475569',
  textTransform: 'uppercase', letterSpacing: 0.5,
  marginBottom: 10, marginTop: 0,
};
const criterionGradeStyle = {
  background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8,
  padding: 14, marginBottom: 10,
};
const feedbackInputStyle = {
  width: '100%', padding: '7px 10px', border: '1px solid #d1d5db',
  borderRadius: 6, fontSize: 13, fontFamily: 'inherit',
  lineHeight: 1.5, resize: 'vertical', boxSizing: 'border-box',
};
const submitBtnStyle = {
  background: '#059669', color: '#fff', border: 'none', borderRadius: 6,
  padding: '10px 24px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
};
const cancelBtnStyle = {
  background: '#fff', color: '#374151', border: '1px solid #d1d5db',
  borderRadius: 6, padding: '9px 18px', fontSize: 13, cursor: 'pointer',
};
const errorBannerStyle = {
  marginTop: 12, padding: '8px 12px', background: '#fef2f2',
  border: '1px solid #fecaca', borderRadius: 6, color: '#991b1b', fontSize: 13,
};
const successBannerStyle = {
  marginTop: 12, padding: '8px 12px', background: '#f0fdf4',
  border: '1px solid #bbf7d0', borderRadius: 6, color: '#166534', fontSize: 13,
};

function statusBadge(status) {
  const colors = {
    pending: { bg: '#fef3c7', fg: '#92400e' },
    ai_graded: { bg: '#dbeafe', fg: '#1e40af' },
    reviewed: { bg: '#dcfce7', fg: '#166534' },
  };
  const c = colors[status] || colors.pending;
  return {
    display: 'inline-block',
    background: c.bg, color: c.fg, fontSize: 11, fontWeight: 600,
    padding: '2px 8px', borderRadius: 999, textTransform: 'capitalize',
  };
}
